"use client";

import type { CSSProperties, ReactNode } from "react";
import { usePathname } from "next/navigation";

import { EnvironmentFrame } from "@/components/layout";
import { useSetupPhase } from "@/contexts/setup-phase-context";

const STRINGS = {
	campaignDetails: "Campaign Details",
} as const;

const OVERLAY_PATHS = new Set([
	"/audiences",
	"/selectpost",
	"/selectcampaign",
	"/mediaupload",
	"/healthcheck",
]);

const FRAMELESS_PATHS = new Set([
	"/content",
	"/conceptdetails",
	"/postdraft",
]);

const ROUTE_HEADINGS: Readonly<Record<string, string>> = {
	"/profilesettings": "Profile Settings",
	"/brandsettings": "Brand Settings",
	"/connectedprofiles": "Connected Profiles",
	"/calendar": "Calendar",
	"/engagement": "Engagement",
	"/content": "Content",
	"/campaigns": "Campaigns",
	"/campaigndetails": STRINGS.campaignDetails,
	"/analytics": "Analytics",
	"/audiences": "Audiences",
	"/notifications": "Notifications",
	"/milestones": "Milestones",
	"/mediaupload": "Media Upload",
	"/postdraft": "Post Draft",
	"/webbrowser": "Web Browser",
};

const VISUALLY_HIDDEN_HEADING_STYLE: CSSProperties = {
	border: 0,
	clip: "rect(0 0 0 0)",
	clipPath: "inset(50%)",
	height: 1,
	margin: -1,
	overflow: "hidden",
	padding: 0,
	position: "absolute",
	whiteSpace: "nowrap",
	width: 1,
};

export function RouteFrame({ children }: { children: ReactNode }) {
	const pathname = usePathname() ?? "";
	const { showBottomNav: setupShowBottomNav } = useSetupPhase();
	const routeHeading = ROUTE_HEADINGS[pathname];
	let routeContent: ReactNode;

	if (pathname === "/" || pathname === "/profilesettings" || pathname === "/brandsettings" || pathname === "/notifications" || pathname === "/milestones" || pathname === "/calendar" || pathname === "/engagement" || FRAMELESS_PATHS.has(pathname) || OVERLAY_PATHS.has(pathname)) {
		routeContent = children;
	} else if (pathname === "/webbrowser") {
		routeContent = <EnvironmentFrame showHeader={false} showBottomNav={false} cropFromFullPage={false}>{children}</EnvironmentFrame>;
	} else if (pathname === "/commentreply") {
		routeContent = <EnvironmentFrame showBottomNav={false}>{children}</EnvironmentFrame>;
	} else {
		routeContent = <EnvironmentFrame showBottomNav={setupShowBottomNav}>{children}</EnvironmentFrame>;
	}

	return (
		<>
			{routeHeading ? (
				<h1
					data-testid="route-semantic-heading"
					data-vastipost-route-heading={pathname}
					style={VISUALLY_HIDDEN_HEADING_STYLE}
				>
					{routeHeading}
				</h1>
			) : null}
			{routeContent}
		</>
	);
}
